import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography } from '@/components/ui/typography';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { cn } from '@/utils/classnames';

// Fixed so the divider holds still across tabs. Behind the flag the column is wide
// enough for the time zone chip on one line, and half of HiringGoal's 816px panel less
// the gap, so the divider sits under the toggle's split.
function usePanelWidth() {
  return useLocalDateRanges() ? 'w-96' : 'w-[240px]';
}

interface DateRangePanelProps {
  /** Main content: an interactive picker or a static date display. */
  children: ReactNode;
  /** The opening's time zone chip, rendered below the main content. */
  timeZoneChip?: ReactNode;
  /** Optional subtitle rendered below the main content in muted small text. */
  subtitle?: ReactNode;
}

export function DateRangePanel({
  children,
  timeZoneChip,
  subtitle,
}: DateRangePanelProps) {
  const width = usePanelWidth();
  return (
    <div className={cn('flex shrink-0 flex-col items-start gap-2', width)}>
      <Typography variant="caption">
        <FormattedMessage
          defaultMessage="Date range"
          description="Caption above a date range display on opening details"
        />
      </Typography>
      {children}
      {timeZoneChip}
      {subtitle && (
        <Typography variant="small1" className="text-fx-neutral-600">
          {subtitle}
        </Typography>
      )}
    </div>
  );
}

DateRangePanel.Skeleton = function DateRangePanelSkeleton({
  withTimeZoneChip = false,
}: {
  /** Holds the chip's place, so the dates do not shift when it paints. */
  withTimeZoneChip?: boolean;
}) {
  const width = usePanelWidth();
  return (
    <div className={cn('flex shrink-0 flex-col items-start gap-2', width)}>
      <div className="bg-fx-neutral-200 h-3 w-20 rounded" />
      <div className="bg-fx-neutral-200 h-5 w-52 rounded" />
      {withTimeZoneChip && (
        <div className="bg-fx-neutral-200 h-6 w-72 rounded-full" />
      )}
      <div className="bg-fx-neutral-200 h-4 w-24 rounded" />
    </div>
  );
};
